import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { AnimatePresence, motion } from 'motion/react';
import React, { useEffect, useState } from 'react';

import { cn } from '@/utils/cn';

import styles from './Slider.module.scss';
import { SliderControls } from './SliderControls';

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

export const Slider: React.FC<{
  HeaderContent?: React.ReactNode;
  showListControlsText?: boolean;
  divider?: boolean;
  label?: string;
  title?: string;
  children: React.ReactNode;
}> = ({
  HeaderContent,
  children,
  divider,
  label,
  showListControlsText,
  title,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const countChildren = React.Children.count(children);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      active: countChildren > 1 ? true : false,
      loop: false,
      align: 'start',
    },
    [WheelGesturesPlugin()]
  );

  useEffect(() => {
    if (!emblaApi) return;

    const updateScrollState = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    emblaApi.on('select', updateScrollState);
    updateScrollState();

    emblaApi.on('select', () => {
      setActiveIndex(emblaApi.selectedScrollSnap());
    });

    return () => {
      emblaApi.off('select', updateScrollState);
    };
  }, [emblaApi]);

  return (
    <>
      <div className={styles.header}>
        {HeaderContent ? (
          HeaderContent
        ) : (
          <div className={styles.headerContent}>
            {label && <span className={styles.name}>{label}</span>}
            {title && <div className={cn(styles.title, 'h4')}>{title}</div>}
          </div>
        )}
        {divider && <div className={styles.headerDivider} />}
        {countChildren > 1 && (
          <SliderControls
            canScrollPrev={canScrollPrev}
            canScrollNext={canScrollNext}
            emblaApi={emblaApi}
            showListControlsText={showListControlsText}
            activeIndex={activeIndex}
            childrenCount={countChildren}
          />
        )}
      </div>
      <div ref={emblaRef}>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={containerVariants}
          className={styles.list}
        >
          <AnimatePresence>{children}</AnimatePresence>
        </motion.div>
      </div>

      {countChildren > 1 && (
        <div className={styles.footer}>
          <SliderControls
            canScrollPrev={canScrollPrev}
            canScrollNext={canScrollNext}
            showListControlsText={showListControlsText}
            activeIndex={activeIndex}
            childrenCount={countChildren}
            emblaApi={emblaApi}
          />
        </div>
      )}
    </>
  );
};
